class HeaderStop {
	get backgroundTop() {
		return `calc((${this.position} - var(--scroll)) * 1px)`;
	}

	get position() {
		return this.element.offsetTop;
	}

	/**
	 * @param {HeaderStop} [next] 
	 */
	backgroundGradient(next) {
		return `${this.backgroundColor} ${this.backgroundTop}, ${this.backgroundColor} ${next ? next.backgroundTop : ""}`;
	}

	textGradient(next) {
		return `${this.backgroundColor} ${this.backgroundTop}, ${this.backgroundColor} ${next ? next.backgroundTop : ""}`;
	}

	/**
	 * @param {HeaderBackgroundElement} element 
	 */
	constructor(element) {
		this.element = element;
		this.fontColor = element.hasAttribute("color") ? element.getAttribute("color") : "#043089";
		this.backgroundColor = element.hasAttribute("background") ? element.getAttribute("background") : "#fff";
	}
}

class HeaderBackgroundElement extends HTMLElement {
	/**
	 * @type {[HeaderStop[]]}
	 */
	static stops;
	static currentStop = -1;
	static headerElement;

	/**
	 * Updates the colors of the header based on the current section.
	 * Returns `true` if the section has changed.
	 * @returns {boolean}
	 */
	static resolveSectionStyle() {
		if (this.stops) {
			for (let i = this.stops.length - 1; i >= 0; i--) {
				if (this.stops[i].position - 45 <= scrollY) {
					if (i !== this.currentStop) {
						this.headerElement.style.color = this.stops[i].fontColor;
						this.headerElement.style.backgroundColor = this.stops[i].backgroundColor;
						this.currentStop = i;
						return true;
					}

					return false;
				}
			}
		}

		return false;
	}

	static applyGradient() {
		if (this.stops) {
			this.resolveSectionStyle();
		}
	}

	constructor() {
		super();

		if (!HeaderBackgroundElement.headerElement)
			HeaderBackgroundElement.headerElement = document.querySelector("header");

		HeaderBackgroundElement.stops ? HeaderBackgroundElement.stops.push(new HeaderStop(this)) : HeaderBackgroundElement.stops = [new HeaderStop(this)];
	}
}

customElements.define("san-header", HeaderBackgroundElement);

/**
 * A list of all the elements that need to be updated on scroll.
 * @type {HTMLElement[]}
 */
let scrollableElements = [];

/**
 * The current state for animating a new scroll position.
 */
const scrollAnimationState = {
	/**
	 * Indicates whether or not the animation is currently running.
	 * @type {boolean}
	 */
	running: false,

	/**
	 * Start time of the animation in milliseconds.
	 * @type {number}
	 */
	startTime: -1,

	/**
	 * End time of the animation in milliseconds.
	 * @type {number}
	 */
	endTime: -1,

	/**
	 * scrollableElements index to start updating from.
	 * @type {number}
	 */
	startIndex: -1,
	
	/**
	 * Updates scrollable elements every animation frame while the current time is less then the end time.
	 */
	animate: () => {
		if (scrollAnimationState.running) {
			const time = performance.now();

			if (time < scrollAnimationState.endTime) {
				updateScrollableElements(scrollAnimationState.startIndex);
				requestAnimationFrame(scrollAnimationState.animate);
			} else {
				scrollAnimationState.running = false;
			}
		}
	},

	/**
	 * Starts the scroll animation, and updates the animation state.
	 * @param {number} duration The duration of the animation.
	 * @param {HTMLElement} [startElement] to start updating scroll position from. Must be in `scrollableElements`.
	 */
	begin: (duration, startElement = null) => {
		scrollAnimationState.running = true;
		scrollAnimationState.startTime = performance.now();
		scrollAnimationState.endTime = scrollAnimationState.startTime + duration;
		scrollAnimationState.startIndex = scrollableElements.indexOf(startElement);

		requestAnimationFrame(scrollAnimationState.animate);
	}
}

/**
 * Attaches an update-scroll-position event to all elements in `scrollableElements`.
 */
const attacheScrollableListeners = () => {
	for (let element of scrollableElements) {
		const index = scrollableElements.indexOf(element);

		element.addEventListener("update-scroll-position", () => {
			updateScrollableElements(index);
		});
	}
}

/**
 * Selects all elements with the "scrollable" attribute, and saves them into `scrollableElements`.
 */
const loadScrollableElements = () => {
	scrollableElements = Array.from(document.querySelectorAll("[scrollable]"));
}

/**
 * Updates the position of all elements in `scrollableElements`.
 * @param {number} [startIndex] Index to start updating elements from.
 */
const updateScrollableElements = (startIndex = 0) => {
	document.body.style.setProperty("--screen", innerHeight);

	/**
	 * @param {HTMLElement} element 
	 * @returns {number}
	 */
	const resolveOffsetTop = element => {
		return element.offsetTop + (element.offsetParent !== document.body ? resolveOffsetTop(element.offsetParent) : 0);
	}

	for (let element of scrollableElements.slice(startIndex)) {
		if (element.offsetParent) {
			let origin = 0;

			switch(element.getAttribute("scrollable")) {
				case "top":
				default:
					origin = resolveOffsetTop(element);
					break;
				case "center":
					origin = resolveOffsetTop(element) + element.clientHeight / 2;
					break;
				case "bottom":
					origin = resolveOffsetTop(element) + element.clientHeight;
					break;
			}

			element.style.setProperty("--position", origin);
			element.style.setProperty("--range", element.clientHeight);
		}
	}

	requestAnimationFrame(() => HeaderBackgroundElement.applyGradient());
}

//
// Handles for scrolling style updates. More performant that DOM updates.
//
const scrollingStylesheetElement = document.createElement("style");

document.head.append(scrollingStylesheetElement);

const scrollingStylesheet = scrollingStylesheetElement.sheet;

scrollingStylesheet.insertRule(`body { --scroll: 0; }`, 0);

let currentScrollY = -1;

/**
 * Updates the scroll position for the current page. Then resolves the header font color.
 */
const updateScrollPosition = () => {
	HeaderBackgroundElement.resolveSectionStyle();

	if (currentScrollY !== scrollY) {
		scrollingStylesheet.cssRules.item(0).style.setProperty("--scroll", scrollY);
		currentScrollY = scrollY;
	}
}

const scrollAnimationFrame = () => {
	updateScrollPosition();
	requestAnimationFrame(scrollAnimationFrame);
}

// addEventListener("scroll", updateScrollPosition, { passive: true });
addEventListener("resize", () => {
	updateScrollableElements();
	updateScrollPosition();
});

addEventListener("DOMContentLoaded", () => {
	requestAnimationFrame(scrollAnimationFrame);

	loadScrollableElements();
	updateScrollableElements();
});

addEventListener("load", () => {
	loadScrollableElements();
	updateScrollableElements();
});

document.querySelector("header .toggle").addEventListener("click", () => {
	document.querySelector("header").classList.toggle("open");
});